import { useEffect, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";
import {
  SearchFilterErrors,
  SearchFilterRef,
  SearchPayload,
} from "../../../core/common/ManagerSingleSearchFilter/types";
import { useNavigate } from "react-router";
import { PaginationType } from "../../../types/common-type";
import useApi from "../../../core/customhook/useApi";
import SearchFilter from "../../../core/common/ManagerSingleSearchFilter/SearchFilter";
import Datatable from "../../../core/common/dataTable";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import { safeLocaleCompare } from "../../../core/methods/sorterMethods";
import Loading from "../../../core/common/custom-loading/Loading";
import dayjs from "dayjs";
import webservice_url from "../../../core/webservice/Webservice_url";
import TimelineModal from "../../../core/common/TimelineModal";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../../admin/utils/filterByColumnSearch";

const initialSearchData: SearchPayload = {
  org_ids: [],
  project_ids: [],
  location_name: [],
  program_ids: [],
  department_ids: [],
};

const InsuffExpired = ({ status_id }: { status_id?: string }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<SearchFilterErrors>({});
  const [itemList, setItemList] = useState([]);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const filterRef = useRef<SearchFilterRef>(null);
  const [searchData, setSearchData] =
    useState<SearchPayload>(initialSearchData);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000, // limit of records
    total: 1,
  });

  type InviteItem = any;
  // const filteredData = (Array.isArray(itemList) ? itemList : []).filter(
  //   (item: InviteItem) => {
  //     return Object.entries(inventoryColumnSearch).every(([key, value]) => {
  //       if (!value) return true;

  //       return String(item[key as keyof InviteItem] ?? "")
  //         .toLowerCase()
  //         .includes(value.toLowerCase());
  //     });
  //   },
  // );

  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
  );

  const liveCaseProgress = useApi<any>(
    "/api/v1/agency/manager/hr-action-required",
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const payload = {
        status: status_id,
        ...searchData,
        page: pagination?.current ?? 1,
        limit: pagination?.pageSize ?? 10,
      };
      const res = await liveCaseProgress.callApi(payload);

      if (!res?.success) {
        setItemList([]);
        return;
      }

      setItemList(res.data?.rows ?? []);
      setPagination((prev) => ({
        ...prev,
        total: res?.data?.pagination?.totalRecords || 0,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const validateFilters = (): boolean => {
    if (searchData.org_ids.length === 0) {
      setErrors({ org_ids: "Organization is required" });
      filterRef.current?.focusField("org_id");
      return false;
    }

    if (searchData.project_ids.length === 0) {
      setErrors({ project_ids: "Account is required" });
      filterRef.current?.focusField("project_id");
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSearch = () => {
    if (!validateFilters()) return;
    fetchCases();
  };

  useEffect(() => {
    if (!status_id) return;
    fetchCases();
  }, [status_id, pagination?.current, pagination?.pageSize]);

  const handleFilterChange = (data: SearchPayload) => {
    const orgChanged = data.org_ids.join(",") !== searchData.org_ids.join(",");

    setSearchData(data);
    setErrors((prev) => ({
      org_ids: data.org_ids.length > 0 ? undefined : prev.org_ids,
      project_ids:
        data.project_ids.length > 0 || orgChanged
          ? undefined
          : prev.project_ids,
    }));
  };

  const handleClear = () => {
    filterRef.current?.clear();
    setErrors({});
  };

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Stopped_Checks_Report",
      sheetName: "Stopped Checks",
    });
  };

  const loading = liveCaseProgress.loading;

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
     onCell: () => cellWidth(200),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      key: "applicant_name",
      onCell: () => cellWidth(200),

      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },
    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: getSearchableHeader(
        "Spoc Name",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "email_id"),
    },

    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Email ID",
        "email_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "email_id",
      key: "email_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "email_id"),
    },
    // {
    //   title: "Insufficiency Type",
    //   dataIndex: "insufficiency_type",
    //   key: "insufficiency_type",
    //   onCell: () => cellWidth(200),
    //   sorter: (a, b) => safeLocaleCompare(a, b, "insufficiency_type"),
    // },
    // {
    //   title: "Raise Date",
    //   dataIndex: "raise_date",
    //   key: "raise_date",
    //   onCell: () => cellWidth(200),
    //   render: (value: string) => formatDate(value),
    // },
    // {
    //   title: "Elapsed Days",
    //   dataIndex: "days_elapsed",
    //   key: "days_elapsed",
    //   onCell: () => cellWidth(200),
    //   sorter: (a, b) => safeLocaleCompare(a, b, "days_elapsed"),
    // },
    {
      title: getSearchableHeader(
        "Check Name",
        "check_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "check_name",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "check_name"),
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Stopped At",
        "stopped_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "stopped_at",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "stopped_at"),
      render: (e: any) => dayjs().format("DD-MM-YYYY"),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(200),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  return (
    <div className="px-2">
      {/* filters */}

      <div className="card shadow-sm border-0">
        <div className="card-body">
          <SearchFilter
            ref={filterRef}
            value={searchData}
            onChange={handleFilterChange}
            storageKey="stoppedCases"
            errors={errors}
          />

          {/* Buttons */}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              className="btn btn-primary px-4"
              onClick={handleSearch}
              // disabled={searchCaseAPI.loading}
            >
              <i className="ti ti-search me-1"></i>
              Search
              {/* {liveCaseProgress.loading ? "Searching..." : "Search"} */}
            </button>

            <button
              className="btn btn-outline-secondary px-4"
              onClick={handleClear}
            >
              <i className="ti ti-refresh me-1"></i>
              Clear Filter
            </button>
          </div>
        </div>
      </div>
      {/* Table  */}
      <div className="card mt-3">
        {liveCaseProgress.loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
  );
};

export default InsuffExpired;
